import React, { useState, useEffect } from 'react'
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Modal,
  Form,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Badge,
  Avatar,
  Typography,
  Switch
} from 'antd'
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ShopOutlined,
  UserOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  UserAddOutlined
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { merchantApi, merchantMemberApi } from '@/services/api'

const { Search } = Input

interface Merchant {
  id: number
  uuid: string
  merchant_id: string
  company_name: string
  industry: string
  contact_person: string
  contact_phone: string
  contact_email: string
  status: 'active' | 'inactive' | 'pending' | 'suspended'
  is_verified: boolean
  address: string
  city: string
  region: string
  rating: number
  total_orders: number
  monthly_revenue: number
  created_at: string
  updated_at: string
  metadata: any
}

const MerchantManagement: React.FC = () => {
  const [merchants, setMerchants] = useState<Merchant[]>([])
  const [loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingMerchant, setEditingMerchant] = useState<Merchant | null>(null)
  // 分页状态
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 })
  // 统计状态
  const [merchantStats, setMerchantStats] = useState({
    total: 0,
    active: 0,
    pending: 0,
    verified: 0
  })

  // 成员管理相关状态
  const [membersModalVisible, setMembersModalVisible] = useState(false)
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null)
  const [members, setMembers] = useState([])
  const [membersLoading, setMembersLoading] = useState(false)
  const [addMemberModalVisible, setAddMemberModalVisible] = useState(false)
  const [addMemberForm] = Form.useForm()

  const [merchantForm] = Form.useForm()

  // 获取商家列表
  const fetchMerchants = async () => {
    setLoading(true)
    try {
      const params: any = {
        page: pagination.current,
        page_size: pagination.pageSize,
        search: searchText || undefined,
        status: statusFilter || undefined,
        include_deleted: includeDeleted
      }
      const response = await merchantApi.getMerchants(params)
      const items = response.data.items || []
      const total = response.data.total || 0
      setMerchants(items)
      setPagination(prev => ({ ...prev, total }))
    } catch (error) {
      message.error('获取商家列表失败')
    } finally {
      setLoading(false)
    }
  }

  const fetchMerchantSummary = async () => {
    try {
      const response = await merchantMemberApi.getMerchantSummary()
      const data = response.data || {}
      setMerchantStats({
        total: data.total_merchants ?? 0,
        active: data.active_merchants ?? 0,
        pending: data.pending_merchants ?? 0,
        verified: data.verified_merchants ?? 0
      })
    } catch (error) {
      // 忽略错误，仅不更新统计卡片
    }
  }

  useEffect(() => {
    fetchMerchants()
    fetchMerchantSummary()
  }, [searchText, statusFilter, includeDeleted, pagination.current])

  // 处理搜索
  const handleSearch = (value: string) => {
    setSearchText(value)
  }

  // 处理状态筛选
  const handleStatusFilter = (status: string) => {
    setStatusFilter(status)
  }

  // 处理新建/编辑商家
  const handleEditMerchant = (merchant?: Merchant) => {
    setEditingMerchant(merchant || null)
    if (merchant) {
      merchantForm.setFieldsValue(merchant)
    } else {
      merchantForm.resetFields()
    }
    setModalVisible(true)
  }

  // 处理保存商家
  const handleSaveMerchant = async (values: any) => {
    try {
      if (editingMerchant) {
        await merchantApi.updateMerchant(editingMerchant.id, values)
        message.success('商家更新成功')
      } else {
        await merchantApi.createMerchant(values)
        message.success('商家创建成功')
      }
      setModalVisible(false)
      fetchMerchants()
      fetchMerchantSummary()
    } catch (error) {
      message.error('保存失败')
    }
  }

  // 处理删除商家
  const handleDeleteMerchant = async (id: number) => {
    try {
      await merchantApi.deleteMerchant(id)
      message.success('删除成功')
      fetchMerchants()
      fetchMerchantSummary()
    } catch (error) {
      message.error('删除失败')
    }
  }

  // 处理商家审核
  const handleVerifyMerchant = async (id: number) => {
    try {
      await merchantApi.verifyMerchant(id)
      message.success('审核通过')
      fetchMerchants()
      fetchMerchantSummary()
    } catch (error) {
      message.error('审核失败')
    }
  }

  // 处理打开成员管理
  const handleManageMembers = (merchant: Merchant) => {
    setSelectedMerchant(merchant)
    setMembersModalVisible(true)
    fetchMembers(merchant.id)
  }

  // 获取商家成员列表
  const fetchMembers = async (merchantId: number) => {
    setMembersLoading(true)
    try {
      const response = await merchantMemberApi.getMerchantMembers(merchantId)
      setMembers(response.data.items || [])
    } catch (error) {
      message.error('获取成员列表失败')
    } finally {
      setMembersLoading(false)
    }
  }

  // 处理添加成员
  const handleAddMember = async (values: any) => {
    try {
      await merchantMemberApi.addMerchantMember(selectedMerchant!.id, {
        user_id: parseInt(values.user_identifier), // 假设输入的是用户ID
        role_type: values.role_type
      })
      message.success('成员添加成功')
      setAddMemberModalVisible(false)
      addMemberForm.resetFields()
      if (selectedMerchant) {
        fetchMembers(selectedMerchant.id)
      }
    } catch (error) {
      message.error('添加成员失败')
    }
  }

  // 处理删除成员
  const handleRemoveMember = async (userId: number) => {
    try {
      await merchantMemberApi.removeMerchantMember(selectedMerchant!.id, userId)
      message.success('成员移除成功')
      if (selectedMerchant) {
        fetchMembers(selectedMerchant.id)
      }
    } catch (error) {
      message.error('移除成员失败')
    }
  }

  // 获取状态颜色
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success'
      case 'inactive': return 'default'
      case 'pending': return 'processing'
      case 'suspended': return 'error'
      default: return 'default'
    }
  }

  // 获取状态文本
  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return '正常'
      case 'inactive': return '停用'
      case 'pending': return '待审核'
      case 'suspended': return '暂停'
      default: return status
    }
  }

  // 获取行业颜色
  const getIndustryColor = (industry: string) => {
    const colors: { [key: string]: string } = {
      'entertainment': 'blue',
      'hospitality': 'green',
      'gaming': 'purple',
      'education': 'orange',
      'retail': 'cyan'
    }
    return colors[industry] || 'default'
  }

  const merchantColumns: ColumnsType<Merchant> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id: number) => <Typography.Text copyable>{id}</Typography.Text>
    },
    {
      title: '商家信息',
      key: 'info',
      render: (_, record) => (
        <div className="flex items-center">
          <Avatar
            size={48}
            icon={<ShopOutlined />}
            className="mr-3"
            style={{ backgroundColor: record.is_verified ? '#52c41a' : '#faad14' }}
          />
          <div>
            <div className="font-medium">{record.company_name}</div>
            <div className="text-sm text-gray-500">
              <Tag color={getIndustryColor(record.industry)}>{record.industry}</Tag>
            </div>
          </div>
        </div>
      )
    },
    {
      title: '联系方式',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div className="flex items-center mb-1">
            <UserOutlined className="mr-2 text-gray-400" />
            <span>{record.contact_person}</span>
          </div>
          <div className="flex items-center mb-1">
            <PhoneOutlined className="mr-2 text-gray-400" />
            <span>{record.contact_phone}</span>
          </div>
          <div className="text-sm text-gray-500">{record.contact_email}</div>
        </div>
      )
    },
    {
      title: '地区',
      dataIndex: 'city',
      key: 'city',
      render: (city: string, record: Merchant) => (
        <div>
          <div className="flex items-center">
            <EnvironmentOutlined className="mr-2 text-gray-400" />
            <span>{city}</span>
          </div>
          <div className="text-sm text-gray-500">{record.region}</div>
        </div>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: Merchant) => (
        <div>
          <Badge
            status={getStatusColor(status) as any}
            text={getStatusText(status)}
          />
          {record.is_verified && (
            <div className="mt-1">
              <CheckCircleOutlined className="text-green-500 mr-1" />
              <span className="text-green-600 text-sm">已认证</span>
            </div>
          )}
        </div>
      )
    },
    {
      title: '业绩',
      key: 'performance',
      render: (_, record) => (
        <div>
          <div className="text-sm">
            订单: <span className="font-medium">{record.total_orders ?? 0}</span>
          </div>
          <div className="text-sm">
            月收入: <span className="font-medium text-green-600">¥{(record.monthly_revenue ?? 0).toLocaleString()}</span>
          </div>
          <div className="text-sm">
            评分: <span className="font-medium text-orange-500">{record.rating ?? 0}/5.0</span>
          </div>
        </div>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString()
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditMerchant(record)}
          >
            编辑
          </Button>
          <Button
            type="link"
            icon={<TeamOutlined />}
            onClick={() => handleManageMembers(record)}
          >
            成员管理
          </Button>
          {record.status === 'pending' && !record.is_verified && (
            <Button
              type="link"
              icon={<CheckCircleOutlined />}
              onClick={() => handleVerifyMerchant(record.id)}
            >
              审核
            </Button>
          )}
          <Popconfirm
            title="确定要删除这个商家吗？"
            onConfirm={() => handleDeleteMerchant(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div className="p-6">
      {/* 页面标题 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">商家管理</h1>
        <p className="text-gray-600">管理平台上的商家信息，确保服务质量</p>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} className="mb-6">
        <Col span={6}>
          <Card>
            <Statistic
              title="总商家数"
              value={merchantStats.total}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="正常运营"
              value={merchantStats.active}
              valueStyle={{ color: '#389e0d' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="待审核"
              value={merchantStats.pending}
              valueStyle={{ color: '#d48806' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="已认证"
              value={merchantStats.verified}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 搜索和筛选 */}
      <Card className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-4">
            <Search
              placeholder="搜索商家名称"
              allowClear
              enterButton={<SearchOutlined />}
              style={{ width: 300 }}
              onSearch={handleSearch}
            />
            <select
              className="border rounded px-3 py-2"
              style={{ width: 150 }}
              onChange={(e) => handleStatusFilter(e.target.value)}
              defaultValue=""
            >
              <option value="">全部状态</option>
              <option value="active">正常</option>
              <option value="pending">待审核</option>
              <option value="inactive">停用</option>
              <option value="suspended">暂停</option>
            </select>
            <div className="flex items-center gap-2">
              <Switch
                size="small"
                checked={includeDeleted}
                onChange={setIncludeDeleted}
              />
              <span className="text-sm text-gray-500">显示已删除</span>
            </div>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleEditMerchant()}
          >
            新建商家
          </Button>
        </div>

        {/* 商家列表 */}
        <Table
          columns={merchantColumns}
          dataSource={merchants}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({ current: page, pageSize: pageSize || 10, total: prev.total }))
            },
          }}
        />
      </Card>

      {/* 新建/编辑商家模态框 */}
      <Modal
        title={
          <span>
            {editingMerchant ? '编辑商家' : '新建商家'}
            {editingMerchant && (
              <Typography.Text type="secondary" className="ml-3 text-sm">
                ID: {editingMerchant.id}
              </Typography.Text>
            )}
          </span>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={600}
        footer={null}
      >
        <Form
          form={merchantForm}
          layout="vertical"
          onFinish={handleSaveMerchant}
        >
          <Form.Item
            label="公司名称"
            name="company_name"
            rules={[{ required: true, message: '请输入公司名称' }]}
          >
            <Input placeholder="输入公司名称" />
          </Form.Item>

          <Form.Item
            label="行业类型"
            name="industry"
            rules={[{ required: true }]}
          >
            <select className="w-full border rounded px-3 py-2">
              <option value="entertainment">娱乐</option>
              <option value="hospitality">酒店</option>
              <option value="gaming">游戏</option>
              <option value="education">教育</option>
              <option value="retail">零售</option>
            </select>
          </Form.Item>

          <Form.Item
            label="联系人"
            name="contact_person"
            rules={[{ required: true }]}
          >
            <Input placeholder="联系人姓名" />
          </Form.Item>

          <Form.Item
            label="联系电话"
            name="contact_phone"
            rules={[{ required: true }]}
          >
            <Input placeholder="联系电话" />
          </Form.Item>

          <Form.Item
            label="联系邮箱"
            name="contact_email"
            rules={[{ required: true, type: 'email' }]}
          >
            <Input placeholder="联系邮箱" />
          </Form.Item>

          <Form.Item
            label="地址"
            name="address"
            rules={[{ required: true }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="详细地址"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                保存
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 成员管理Modal */}
      <Modal
        title={`成员管理 - ${selectedMerchant?.company_name}`}
        open={membersModalVisible}
        onCancel={() => setMembersModalVisible(false)}
        footer={null}
        width={800}
      >
        <div className="mb-4">
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => setAddMemberModalVisible(true)}
          >
            添加成员
          </Button>
        </div>

        <Table
          dataSource={members}
          loading={membersLoading}
          rowKey="id"
          pagination={false}
        >
          <Table.Column
            title="用户名"
            key="username"
            render={(_, record: any) => (
              <div className="flex items-center">
                <Avatar
                  src={record.user?.avatar_url}
                  icon={!record.user?.avatar_url ? <UserOutlined /> : null}
                  className="mr-2"
                />
                <div>
                  <div className="font-medium">{record.user?.username || 'N/A'}</div>
                  <div className="text-sm text-gray-500">{record.user?.email || 'N/A'}</div>
                  {record.user?.nickname && (
                    <div className="text-xs text-gray-400">{record.user.nickname}</div>
                  )}
                </div>
              </div>
            )}
          />
          <Table.Column
            title="角色"
            dataIndex="role_type"
            key="role_type"
            render={(role: string) => (
              <Tag color={
                role === 'owner' ? 'red' :
                role === 'admin' ? 'orange' :
                role === 'operator' ? 'blue' :
                'default'
              }>
                {role === 'owner' ? '所有者' :
                 role === 'admin' ? '管理员' :
                 role === 'operator' ? '运营' :
                 role === 'viewer' ? '查看者' : role}
              </Tag>
            )}
          />
          <Table.Column
            title="状态"
            dataIndex="is_active"
            key="is_active"
            render={(isActive: boolean) => (
              <Badge
                status={isActive ? 'success' : 'default'}
                text={isActive ? '活跃' : '非活跃'}
              />
            )}
          />
          <Table.Column
            title="分配时间"
            dataIndex="assigned_at"
            key="assigned_at"
            render={(date: string) => new Date(date).toLocaleDateString()}
          />
          <Table.Column
            title="操作"
            key="action"
            render={(_, record: any) => (
              <Space>
                <Button
                  type="link"
                  size="small"
                  onClick={() => {
                    // TODO: 编辑角色
                    console.log('编辑角色', record)
                  }}
                >
                  编辑
                </Button>
                <Popconfirm
                  title="确定要移除这个成员吗？"
                  onConfirm={() => handleRemoveMember(record.user_id)}
                  okText="确定"
                  cancelText="取消"
                >
                  <Button type="link" size="small" danger>
                    移除
                  </Button>
                </Popconfirm>
              </Space>
            )}
          />
        </Table>
      </Modal>

      {/* 添加成员Modal */}
      <Modal
        title="添加成员"
        open={addMemberModalVisible}
        onCancel={() => {
          setAddMemberModalVisible(false)
          addMemberForm.resetFields()
        }}
        footer={null}
      >
        <Form
          form={addMemberForm}
          layout="vertical"
          onFinish={handleAddMember}
        >
          <Form.Item
            label="用户ID或用户名"
            name="user_identifier"
            rules={[{ required: true, message: '请输入用户ID或用户名' }]}
          >
            <Input placeholder="请输入要添加的用户ID或用户名" />
          </Form.Item>

          <Form.Item
            label="角色"
            name="role_type"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <select
              className="ant-input"
              style={{ width: '100%', padding: '8px 11px' }}
            >
              <option value="">请选择角色</option>
              <option value="admin">管理员</option>
              <option value="operator">运营</option>
              <option value="viewer">查看者</option>
            </select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                添加
              </Button>
              <Button onClick={() => {
                setAddMemberModalVisible(false)
                addMemberForm.resetFields()
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default MerchantManagement
